import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import Modal from '../Modal';
import { SchemaForm } from '../../pages/WindmillAppPage/components/SchemaForm';
import { ResultDisplay } from '../../pages/WindmillAppPage/components/ResultDisplay';
import {
  CancelOutcome,
  CancelRunButton,
} from '../../pages/WindmillAppPage/components/CancelRunButton';
import { useWindmillExecution } from '../../pages/WindmillAppPage/hooks/useWindmillExecution';
import { EmbeddingContext, WindmillApp } from '../../pages/WindmillAppPage/types';

import pageStyles from '../../pages/WindmillAppPage/WindmillAppPage.module.scss';
import styles from './EmbeddedAppRunModal.module.scss';

interface EmbeddedAppRunModalProps {
  app: WindmillApp;
  embedding: EmbeddingContext;
  onClose: () => void;
}

/**
 * Runs an embedded windmill app for one case / message. The placement context
 * is injected server-side, so the modal only asks for the app's remaining
 * input fields — and when there are none the run starts as soon as it opens.
 */
export const EmbeddedAppRunModal: React.FC<EmbeddedAppRunModalProps> = ({
  app,
  embedding,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    run,
    resume,
    applyExecution,
    executionId,
    status,
    output,
    error,
    isRunning,
    durationMs,
    completedAt,
    reset,
  } = useWindmillExecution(app, embedding);

  const hasProperties =
    Object.keys(app.input_schema?.properties || {}).length > 0;

  // A ref, not state: it survives StrictMode's effect re-run, so a no-input
  // app is dispatched exactly once per opening.
  const autoRunStarted = useRef(false);
  useEffect(() => {
    if (hasProperties || autoRunStarted.current) return;
    autoRunStarted.current = true;
    run({});
  }, [hasProperties, run]);

  const handleCancelSettled = useCallback(
    (id: string, outcome: CancelOutcome) => {
      if (id !== executionId) return;
      if (outcome.kind === 'canceled') {
        applyExecution(outcome.execution);
      } else {
        resume(id);
      }
    },
    [applyExecution, executionId, resume],
  );

  const content = (
    <div className={styles.body}>
      {app.description && (
        <p className={styles.description}>{app.description}</p>
      )}

      {hasProperties && (
        <SchemaForm
          schema={app.input_schema}
          fieldOrder={app.field_order}
          onSubmit={run}
          isSubmitting={isRunning}
        />
      )}

      {isRunning && (
        <div className={pageStyles.runningIndicator}>
          <div className={pageStyles.spinner} />
          <span>{t('Running...')}</span>
          {executionId && (
            <CancelRunButton
              executionId={executionId}
              onSettled={handleCancelSettled}
            />
          )}
        </div>
      )}

      {status && !isRunning && (
        <ResultDisplay
          status={status}
          output={output}
          error={error}
          durationMs={durationMs}
          completedAt={completedAt}
          resultDisplay={app.result_display}
          onDismissError={hasProperties ? reset : undefined}
        />
      )}

      {!hasProperties && status && !isRunning && (
        <div className={styles.actions}>
          <button
            type="button"
            className={pageStyles.runButton}
            onClick={() => run({})}
          >
            {t('Run again')}
          </button>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      isVisible
      title={
        <>
          {app.icon && <span className={styles.icon}>{app.icon}</span>}
          {app.name}
        </>
      }
      content={content}
      footer={undefined}
      onClose={onClose}
      className="windmill-embedded-modal"
      doNotCloseOnOutsideClick={isRunning}
    />
  );
};
